import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flex, Text, Box, Tooltip } from '@chakra-ui/react';
import { Accordion, Badge, ButtonIcon } from '@morressier/alembic';
import { Table } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faFile } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { fetchSubmissionNotesHistory } from 'src/api/queries';
import { formatDate } from 'src/utils/date';
import { pluralizeString } from 'src/utils/string';
import { INotesHistoryGroup } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import ViewNoteModal from './ViewNoteModal';

const NoteHistoryGroupTitle = ({ group }: { group: INotesHistoryGroup }) => (
  <Flex alignItems="flex-start" flexDirection="column" gap={1} pr={4}>
    <Flex alignItems="center" gap={2}>
      <Text textStyle="textBody" fontWeight="semibold">
        {group.journalName}
      </Text>
      <Badge variant="neutral" size="context">
        Transferred
      </Badge>
    </Flex>
    <Flex alignItems="center" gap={2}>
      <Text textStyle="textContext" color="gray.600">
        {group.notes.length} {pluralizeString('Note', group.notes.length)}
      </Text>
      {group.transferDate && (
        <>
          <Text color="gray.400" fontSize="xs">
            ·
          </Text>
          <Text textStyle="textContext" color="gray.500">
            Transferred on {formatDate(new Date(group.transferDate))}
          </Text>
        </>
      )}
    </Flex>
  </Flex>
);

const NoteHistoryGroupContent = ({
  group,
  onNoteClick
}: {
  group: INotesHistoryGroup;
  onNoteClick: (note: ISubmissionNotesListItem) => void;
}) => (
  <Box width="100%" overflowX="auto">
    <Table>
      <Table.HeaderRow
        sx={{
          '& th': {
            textTransform: 'none',
            color: 'black',
            fontSize: '14px',
            fontWeight: '500',
            whiteSpace: 'nowrap'
          }
        }}
      >
        <Table.HeaderCell flex="2" textAlign="left">
          Title &amp; Note
        </Table.HeaderCell>
        <Table.HeaderCell flex="1" textAlign="left">
          Added By
        </Table.HeaderCell>
        <Table.HeaderCell flex="1" textAlign="left">
          Last Updated On
        </Table.HeaderCell>
      </Table.HeaderRow>
      <Table.Body>
        {group.notes.map(note => (
          <Table.Row
            key={note.id}
            onClick={() => onNoteClick(note)}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
          >
            <Table.RowCell flex="2" textAlign="left" verticalAlign="top">
              <Flex direction="column" alignItems="flex-start" minWidth={0}>
                <Tooltip
                  hasArrow
                  borderRadius={4}
                  placement="top"
                  label="View Note"
                >
                  <Text
                    textStyle="headingLine"
                    color="#2043DF"
                    _hover={{ textDecoration: 'underline', color: '#142480' }}
                    noOfLines={1}
                  >
                    {note.title?.trim() || 'Untitled'}
                  </Text>
                </Tooltip>
                {note.content && (!note.title || note.title === 'Untitled') && (
                  <Text
                    textStyle="textBody"
                    color="gray.600"
                    noOfLines={1}
                    mt={0.5}
                  >
                    {note.content}
                  </Text>
                )}
                <Flex alignItems="center" gap={2} mt={1} flexWrap="wrap">
                  {note.attachments && note.attachments.length > 0 && (
                    <Flex alignItems="center" gap={1}>
                      <FontAwesomeIcon
                        icon={faFile}
                        size="sm"
                        color="gray.600"
                      />
                      <Text textStyle="textBody" color="gray.600" noOfLines={1}>
                        {note.attachments.length}{' '}
                        {pluralizeString('File', note.attachments.length)}
                      </Text>
                    </Flex>
                  )}
                  {note.category && (
                    <Badge variant="neutral" size="context">
                      {note.category}
                    </Badge>
                  )}
                </Flex>
              </Flex>
            </Table.RowCell>

            <Table.RowCell flex="1" textAlign="left" verticalAlign="top">
              <Text textStyle="headingLine">
                {`${note.addedBy?.firstName ?? ''} ${
                  note.addedBy?.lastName ?? ''
                }`.trim() || '—'}
              </Text>
              {note.mentions && note.mentions.length > 0 && (
                <Text textStyle="textBody" color="gray.500" mt={0.5}>
                  @{note.mentions[0]?.name}
                  {note.mentions.length > 1 && `, +${note.mentions.length - 1}`}
                </Text>
              )}
            </Table.RowCell>

            <Table.RowCell flex="1" textAlign="left" verticalAlign="top">
              <Text textStyle="textBody">
                {format(new Date(note.modified_at), 'MMM dd, yyyy')}
              </Text>
            </Table.RowCell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  </Box>
);

export const NotesHistory: React.FC = () => {
  const { submissionId } = useSubmissionData();
  const ctxId = useCurrentContextId() ?? '';
  const [selectedNote, setSelectedNote] =
    useState<ISubmissionNotesListItem | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['submission-notes-history', { submissionId }],
    queryFn: () =>
      ctxId && submissionId
        ? fetchSubmissionNotesHistory({ ctxId, submissionId })
        : null,
    enabled: !!(ctxId && submissionId)
  });

  const history = (data?.history ?? []).filter(g => g.notes.length > 0);

  if (history.length === 0) return null;

  return (
    <Flex flexDirection="column" gap={4} marginTop={10}>
      <Flex flexDirection="column" gap={2}>
        <Flex alignItems="center" gap={1}>
          <Text textStyle="headingSection" id="notes-history">
            Notes History
          </Text>
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="top"
            label="Notes from previously linked journals for this submission. Read-only."
            borderRadius={4}
          >
            <ButtonIcon
              size="small"
              ml={1}
              ariaLabel="Notes history info"
              icon={<FontAwesomeIcon icon={faInfoCircle} />}
            />
          </Tooltip>
        </Flex>
      </Flex>

      <Accordion
        chakraAccordionProps={{
          allowMultiple: true,
          defaultIndex: [],
          style: { display: 'flex', flexDirection: 'column', gap: '8px' }
        }}
        accordionItemProps={{
          background: 'neutralPrimaryContentInverse',
          marginBottom: 0
        }}
        items={history.map(group => ({
          title: <NoteHistoryGroupTitle group={group} />,
          content: (
            <NoteHistoryGroupContent
              group={group}
              onNoteClick={note => {
                setSelectedNote(note);
                setIsViewModalOpen(true);
              }}
            />
          )
        }))}
      />

      {selectedNote && (
        <ViewNoteModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedNote(null);
          }}
          data={selectedNote}
          readOnly
        />
      )}
    </Flex>
  );
};
